import { readonly, unref, ref, computed } from "vue";
// eslint-disable-next-line import/no-extraneous-dependencies, import/no-unresolved
import { useMemoize } from "@vueuse/core";
import { jsonClient } from "../lib/raftApi";
import { useModulesEnabled } from "./useModulesEnabled";

// Global ref to store user metadata
const globalUserMetadata = ref([]);

const { modulesEnabled, getModules } = useModulesEnabled();
const path = "/user_metadata/user_metadata";

const getFirstItemIfArray = (payload) => {
  if (!!payload && payload.constructor === Array && payload.length > 0) {
    return payload[0];
  }
  if (!!payload && payload.constructor === Object) {
    return payload;
  }
  return null;
};

const getIdFromResponse = (response) =>
  getFirstItemIfArray(response?.data ?? response)?.id;

const parseChildren = (payload) => {
  if (!!payload && payload.constructor === Array) {
    return payload.map((child) => {
      if (!!child && child.constructor === Object) {
        const ret = { ...child };
        try {
          ret.json = JSON.parse(ret.json);
        } catch (e) {
          ret.err = e;
        }
        return ret;
      }
      return child;
    });
  }
  return payload;
};

// all user_metadata values in globalUserMetadata
const sessionMetadataList = computed(() =>
  parseChildren(globalUserMetadata.value),
);

const handleAxiosError = (error) =>
  getModules().then(() => {
    if (modulesEnabled.value.user_metadata) {
      if (error?.response?.status) {
        if (error.response.status !== 404) {
          // eslint-disable-next-line
          console.error(error);
        }
      }
      if (error?.message) {
        if (error.message !== "canceled") {
          // eslint-disable-next-line
          console.error(error);
        }
      }
    } else {
      // eslint-disable-next-line
      console.log("user_metadata not enabled");
    }
  });

// the index of the json object within sessionMetadataList
const index = ({ key, route, module }) => {
  const found = ref(-1);
  if (
    !!sessionMetadataList.value &&
    sessionMetadataList?.value?.constructor === Array &&
    sessionMetadataList?.value?.length > 0
  ) {
    found.value = sessionMetadataList?.value?.findIndex(
      (v) => v?.key === key && v?.module === module && v?.route === route,
    );
  }
  return found.value;
};

// the user_metadata object representing 'key' in user_metadata globalUserMetadata
const getPersistedObject = ({ key, route, module }) => {
  const obj = ref(null);
  if (index({ key, route, module }) > -1) {
    obj.value = sessionMetadataList.value[index({ key, route, module })];
  }
  return obj;
};

const fetchMultiple = async ({ keys, route, module }) => {
  const promises = keys.map((key) => fetchMetadata({ key, route, module }));
  let results = await Promise.all(promises);
  if (!!results && results.constructor === Array) {
    results = results.filter((i) => i != null);
  }
  globalUserMetadata.value = results;
};

const updatedSessionMetadataList = ({ key, updatedObj, route, module }) => {
  const newValues = unref(sessionMetadataList) ?? [];
  const existingIndex = index({ key, route, module });
  if (existingIndex > -1) {
    newValues[existingIndex] = unref(updatedObj);
  } else {
    newValues.push(unref(updatedObj));
  }
  return newValues;
};

const formatForJsonApi = ({ key, route, module, newValue }) => {
  const formatted = {
    type: "user-metadata--user-metadata",
    attributes: {
      key,
      route,
      module,
    },
    relationships: {},
  };
  try {
    formatted.attributes.json = JSON.stringify(newValue);
  } catch (e) {
    // eslint-disable-next-line
    console.error(e);
  }
  // if exists, use id
  const current = getPersistedObject({ key, route, module });
  if (current.value?.id) {
    formatted.id = current.value.id;
  }
  return formatted;
};

const removeFromSessionMetadataList = ({ key, route, module }) => {
  const newValues = unref(sessionMetadataList) ?? [];
  const existingIndex = index({ key, route, module });
  if (existingIndex > -1) {
    newValues.splice(existingIndex, 1);
    globalUserMetadata.value = newValues;
  }
};

const set = async ({ key, newValue, route, module }) => {
  if (!key) {
    throw new Error("missing key");
  }
  let obj = unref(getPersistedObject({ key, route, module }));
  obj = {
    ...obj,
    key,
    module,
    route,
    json: newValue,
  };
  // If we don't have an ID for this yet, try fetching.
  if (!obj.hasOwnProperty("id")) {
    const response = await fetchMetadata({ key, route, module });
    const id = getIdFromResponse(response);
    if (id) {
      obj.id = id;
      globalUserMetadata.value = updatedSessionMetadataList({
        key,
        updatedObj: obj,
        route,
        module,
      });
    }
  }
  // If we still didn't get an ID, use a POST.
  if (!obj.hasOwnProperty("id")) {
    return jsonClient
      .post({
        path,
        data: formatForJsonApi({ key, route, module, newValue }),
        queries: {},
      })
      .catch((error) => handleAxiosError(error))
      .then((response) => {
        const id = getIdFromResponse(response);
        if (id) {
          obj = { ...obj, id };
        }
        globalUserMetadata.value = updatedSessionMetadataList({
          key,
          updatedObj: obj,
          route,
          module,
        });
      });
  }
  return jsonClient
    .patch({
      path: `${path}/${obj.id}`,
      data: { ...formatForJsonApi({ key, route, module, newValue }) },
      queries: {},
    })
    .catch((error) => handleAxiosError(error))
    .finally(async () => {
      try {
        await fetchMetadata.load({ key, route, module });
      } catch (e) {
        // eslint-disable-next-line
        console.error(e);
      }
    });
};

const fetchMetadata = useMemoize(async ({ key, route, module }) => {
  const response = await jsonClient
    .get({
      path,
      queries: {
        "filter[route]": route,
        "filter[module]": module,
        "filter[key]": key,
      },
    })
    .then((res) => {
      const flat = getFirstItemIfArray(res.deserialized);
      if (flat !== null) {
        return {
          id: flat.id,
          key: flat.key,
          route: flat.route,
          module: flat.module,
          json: JSON.parse(flat.json),
        };
      }
      return null;
    })
    .catch((error) => handleAxiosError(error));
  if (response) {
    removeFromSessionMetadataList({ key, route, module });
    globalUserMetadata.value.push(response);
  }
  return response;
});

const remove = async ({ key, route, module }) => {
  // check if exists in API
  const response = await fetchMetadata({ key, route, module });
  const serverValue = response?.data?.[0];
  if (serverValue?.id) {
    jsonClient
      .delete({ path: `${path}/${serverValue.id}` })
      .catch((error) => handleAxiosError(error));
  }
  return removeFromSessionMetadataList({ key, route, module });
};

// the (pre-parsed) value of the 'json' key on the relevant object
const get = async ({ key, route, module }) => {
  if (!key) {
    throw new Error("key not defined");
  }
  await fetchMetadata({ key, route, module });
  const valueRef = ref(null);
  const obj = unref(getPersistedObject({ key, route, module }));
  if (!!obj && obj.constructor === Object) {
    valueRef.value = obj.json;
  }
  return valueRef;
};

// eslint-disable-next-line import/prefer-default-export
export function usePersistedValues({ route, module }) {
  const getPersistedValue = (key) => get({ key, route, module });
  const setPersistedValue = ({ key, newValue }) =>
    set({ key, newValue, route, module });
  const deletePersistedValue = (key) => remove({ key, route, module });
  const fetchForRoute = (keys) => fetchMultiple({ keys, route, module });
  return {
    set,
    setPersistedValue,
    remove,
    deletePersistedValue,
    index,
    get,
    getPersistedObject,
    getPersistedValue,
    fetchMetadata,
    fetchForRoute,
    fetchMultiple,
    sessionMetadataList: readonly(sessionMetadataList),
  };
}
